import { observer } from 'mobx-react-lite';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Autocomplete,
    AutocompleteRenderInputParams,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Paper,
    TextField,
    Typography
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { LineListTemplate } from '../../../../entity/model/line-list-template';
import { LineListTemplateDialogProps } from '../../../../entity/props/report/line-list-template-dialog-props';
import { LineListTemplateDto } from '../../../../entity/dto/line-list-template-dto';
import { useService } from '../../../../infrastructure/service/service';
import { useState, useEffect } from 'react';
import { ManageMode } from '../../../../entity/enums/manage-mode';
import LoadingComponent from '../widget/LoadingComponent';

import '../../../../assets/css/style.css';
import '../../../../assets/css/reports/line-list-template-dialog.css';

const LineListTemplateDialog = (props: LineListTemplateDialogProps): JSX.Element => {
    const { lineListTemplateService, userService } = useService();
    const [sectionsAccordion, setSectionsAccordion] = useState<boolean>(true);
    const [templates, setTemplates] = useState<LineListTemplate[]>([]);
    const [filteredTemplates, setFilteredTemplates] = useState<LineListTemplate[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [isLoadingTemplates, setIsLoadingTemplates] = useState<boolean>(false);

    const mode = props.mode ?? ManageMode.EXPORT;

    useEffect(() => {
        if (mode === ManageMode.IMPORT && props.open)
            loadTemplates();
    }, [mode, props.open]);

    useEffect(() => {
        if (searchQuery.trim() === '')
            setFilteredTemplates(templates);
        else {
            const filtered = lineListTemplateService.filterTemplates(searchQuery);
            setFilteredTemplates(filtered);
        }
    }, [searchQuery, templates]);

    const loadTemplates = async (): Promise<void> => {
        setIsLoadingTemplates(true);
        const user = await userService.getCurrentUser();
        if (user?.organizationID) {
            const templatesData = await lineListTemplateService.getLineListTemplates(user.organizationID);
            setTemplates(templatesData);
            setFilteredTemplates(templatesData);
        }
        setIsLoadingTemplates(false);
    }

    const handleTemplateSelect = (template: LineListTemplate): void => {
        if (props.onTemplateSelect)
            props.onTemplateSelect(template);
        props.onClose();
    }

    const handleTemplateCancel = (): void => {
        props.onClose();
    }

    const handleValidate = (values: LineListTemplateDto): Record<string, string> => {
        const errors: Record<string, string> = {};

        if (!values.templateName.trim())
            errors.templateName = 'Template name is required.';

        const hasSelectedSections = Object.values(values).slice(1).some(Boolean);
        if (!hasSelectedSections)
            errors.sectionSelection = 'At least one section must be selected.';

        return errors;
    }

    const handleTemplateSubmit = async (values: LineListTemplateDto, { setSubmitting }: FormikHelpers<LineListTemplateDto>): Promise<void> => {
        if (!values.templateName.trim() || Object.values(values).slice(1).every((v) => !v)) {
            setSubmitting(false);
            return;
        }

        const currentUser = await userService.getCurrentUser();
        const template: LineListTemplate = {
            organizationID: currentUser?.organizationID ?? '',
            templateName: values.templateName.trim(),
            comment: values.includeGeneralComments ? props.lineListRequest.comment : undefined,
            orderInformation: values.includeOrderInfo ? props.lineListRequest.orderInformation : undefined,
            propertyInformation: values.includePropertyInfo ? props.lineListRequest.propertyInformation : undefined,
            ownerInformation: values.includeOwnerInfo ? props.lineListRequest.ownerInformation : undefined,
            occupantInformation: values.includeOccupantInfo ? props.lineListRequest.occupantInformation : undefined,
            acquisitionDetails: values.includeAcquisitionDetails ? props.lineListRequest.acquisitionDetails : undefined,
            vestingDeed: values.includeVestingDeed ? props.lineListRequest.vestingDeed : undefined,
            estates: values.includeEstates ? props.lineListRequest.estates : undefined,
            taxYears: values.includeTaxes ? props.lineListRequest.taxYears : undefined,
            taxFlags: values.includeTaxes ? props.lineListRequest.taxFlags : undefined,
            mobileHome: values.includeMobileHome ? props.lineListRequest.mobileHome : undefined,
            restrictionsAgreements: values.includeRestrictions ? props.lineListRequest.restrictionsAgreements : undefined,
            easementsRightOfWays: values.includeEasements ? props.lineListRequest.easementsRightOfWays : undefined,
        };

        await lineListTemplateService.createLineListTemplate(currentUser?.organizationID ?? '', template);
        props.onClose();
        setSubmitting(false);
    }

    const handleSelectAll = (handleChange: any): void => {
        handleChange({
            target: {
                name: 'includeOrderInfo',
                value: true
            }
        });
        handleChange({
            target: {
                name: 'includePropertyInfo',
                value: true
            }
        });
        handleChange({
            target: {
                name: 'includeOwnerInfo',
                value: true
            }
        });
        handleChange({
            target: {
                name: 'includeOccupantInfo',
                value: true
            }
        });
        handleChange({
            target: {
                name: 'includeAcquisitionDetails',
                value: true
            }
        });
        handleChange({
            target: {
                name: 'includeVestingDeed',
                value: true
            }
        });
        handleChange({
            target: {
                name: 'includeEstates',
                value: true
            }
        });
        handleChange({
            target: {
                name: 'includeTaxes',
                value: true
            }
        });
        handleChange({
            target: {
                name: 'includeMobileHome',
                value: true
            }
        });
        handleChange({
            target: {
                name: 'includeRestrictions',
                value: true
            }
        });
        handleChange({
            target: {
                name: 'includeEasements',
                value: true
            }
        });
        handleChange({
            target: {
                name: 'includeGeneralComments',
                value: true
            }
        });
    }

    const handleClearAll = (handleChange: any): void => {
        handleChange({
            target: {
                name: 'includeOrderInfo',
                value: false
            }
        });
        handleChange({
            target: {
                name: 'includePropertyInfo',
                value: false
            }
        });
        handleChange({
            target: {
                name: 'includeOwnerInfo',
                value: false
            }
        });
        handleChange({
            target: {
                name: 'includeOccupantInfo',
                value: false
            }
        });
        handleChange({
            target: {
                name: 'includeAcquisitionDetails',
                value: false
            }
        });
        handleChange({
            target: {
                name: 'includeVestingDeed',
                value: false
            }
        });
        handleChange({
            target: {
                name: 'includeEstates',
                value: false
            }
        });
        handleChange({
            target: {
                name: 'includeTaxes',
                value: false
            }
        });
        handleChange({
            target: {
                name: 'includeMobileHome',
                value: false
            }
        });
        handleChange({
            target: {
                name: 'includeRestrictions',
                value: false
            }
        });
        handleChange({
            target: {
                name: 'includeEasements',
                value: false
            }
        });
        handleChange({
            target: {
                name: 'includeGeneralComments',
                value: false
            }
        });
    }

    const handleSelectAllChange = (values: LineListTemplateDto, handleChange: any) => (event: any): void => {
        if (event.target.checked)
            handleSelectAll(handleChange);
        else
            handleClearAll(handleChange);
    }

    const renderExportMode = (): JSX.Element => (
        <Formik
            initialValues={{
                templateName: '',
                includeOrderInfo: false,
                includePropertyInfo: false,
                includeOwnerInfo: false,
                includeOccupantInfo: false,
                includeAcquisitionDetails: false,
                includeVestingDeed: false,
                includeEstates: false,
                includeTaxes: false,
                includeMobileHome: false,
                includeRestrictions: false,
                includeEasements: false,
                includeGeneralComments: false
            }}
            enableReinitialize={true}
            validate={handleValidate}
            onSubmit={handleTemplateSubmit}>
            {({ values, errors, touched, handleChange, handleBlur, isSubmitting }: FormikProps<LineListTemplateDto>): JSX.Element => {
                const allSelected = Object.values(values).slice(1).every(Boolean);

                return (
                    <Form>
                        <DialogContent dividers>
                            <Paper elevation={0}>
                                <Box className='line-list-template-name-section'>
                                    <TextField
                                        fullWidth
                                        required
                                        name='templateName'
                                        label='Template Name'
                                        value={values.templateName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder='e.g., Standard Residential, Commercial Property, etc.'
                                        autoFocus
                                        error={touched.templateName && Boolean(errors.templateName)}
                                        helperText={touched.templateName && errors.templateName}
                                        className='zitles-textfield' />
                                </Box>

                                <Accordion
                                    elevation={0}
                                    className='template-dialog-card'
                                    expanded={sectionsAccordion}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon onClick={(): void => setSectionsAccordion(!sectionsAccordion)} className='dialog-icon' />}>
                                        <Typography className='template-data-value accordion'>Sections</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box className='checkbox-template-container header template-checkbox'>
                                            <Checkbox
                                                checked={allSelected}
                                                onChange={handleSelectAllChange(values, handleChange)} />
                                            <Typography className='checkbox-template-container title'>
                                                {allSelected ? 'Deselect All' : 'Select All'}
                                            </Typography>
                                        </Box>

                                        {
                                            (errors as any).sectionSelection &&
                                            <Typography variant='body2' color='error' className='zitles-form-error'>
                                                {(errors as any).sectionSelection}
                                            </Typography>
                                        }

                                        <Grid container spacing={3} className='line-list-template-sections-grid'>
                                            <Grid size={12}>
                                                <Box className='line-list-template-section-column'>
                                                    <Box className='checkbox-template-container template-checkbox'>
                                                        <Checkbox
                                                            name='includeOrderInfo'
                                                            checked={values.includeOrderInfo}
                                                            onChange={handleChange} />
                                                        <Typography className='checkbox-template-container title'>Order Information</Typography>
                                                    </Box>
                                                    <Box className='checkbox-template-container template-checkbox'>
                                                        <Checkbox
                                                            name='includePropertyInfo'
                                                            checked={values.includePropertyInfo}
                                                            onChange={handleChange} />
                                                        <Typography className='checkbox-template-container title'>Property Information</Typography>
                                                    </Box>
                                                    <Box className='checkbox-template-container template-checkbox'>
                                                        <Checkbox
                                                            name='includeOwnerInfo'
                                                            checked={values.includeOwnerInfo}
                                                            onChange={handleChange} />
                                                        <Typography className='checkbox-template-container title'>Owner Information</Typography>
                                                    </Box>
                                                    <Box className='checkbox-template-container template-checkbox'>
                                                        <Checkbox
                                                            name='includeOccupantInfo'
                                                            checked={values.includeOccupantInfo}
                                                            onChange={handleChange} />
                                                        <Typography className='checkbox-template-container title'>Occupant Information</Typography>
                                                    </Box>
                                                    <Box className='checkbox-template-container template-checkbox'>
                                                        <Checkbox
                                                            name='includeAcquisitionDetails'
                                                            checked={values.includeAcquisitionDetails}
                                                            onChange={handleChange} />
                                                        <Typography className='checkbox-template-container title'>Acquisition Details</Typography>
                                                    </Box>
                                                    <Box className='checkbox-template-container template-checkbox'>
                                                        <Checkbox
                                                            name='includeVestingDeed'
                                                            checked={values.includeVestingDeed}
                                                            onChange={handleChange} />
                                                        <Typography className='checkbox-template-container title'>Vesting Deed</Typography>
                                                    </Box>
                                                    <Box className='checkbox-template-container template-checkbox'>
                                                        <Checkbox
                                                            name='includeEstates'
                                                            checked={values.includeEstates}
                                                            onChange={handleChange} />
                                                        <Typography className='checkbox-template-container title'>Estates</Typography>
                                                    </Box>
                                                    <Box className='checkbox-template-container template-checkbox'>
                                                        <Checkbox
                                                            name='includeTaxes'
                                                            checked={values.includeTaxes}
                                                            onChange={handleChange} />
                                                        <Typography className='checkbox-template-container title'>Taxes</Typography>
                                                    </Box>
                                                    <Box className='checkbox-template-container template-checkbox'>
                                                        <Checkbox
                                                            name='includeMobileHome'
                                                            checked={values.includeMobileHome}
                                                            onChange={handleChange} />
                                                        <Typography className='checkbox-template-container title'>Mobile Home</Typography>
                                                    </Box>
                                                    <Box className='checkbox-template-container template-checkbox'>
                                                        <Checkbox
                                                            name='includeRestrictions'
                                                            checked={values.includeRestrictions}
                                                            onChange={handleChange} />
                                                        <Typography className='checkbox-template-container title'>Restrictions / Agreements</Typography>
                                                    </Box>
                                                    <Box className='checkbox-template-container template-checkbox'>
                                                        <Checkbox
                                                            name='includeEasements'
                                                            checked={values.includeEasements}
                                                            onChange={handleChange} />
                                                        <Typography className='checkbox-template-container title'>Easements & Right of Ways</Typography>
                                                    </Box>
                                                    <Box className='checkbox-template-container template-checkbox'>
                                                        <Checkbox
                                                            name='includeGeneralComments'
                                                            checked={values.includeGeneralComments}
                                                            onChange={handleChange} />
                                                        <Typography className='checkbox-template-container title'>General Comments</Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            </Paper>
                        </DialogContent>

                        <DialogActions className='action-btn-container'>
                            <Button
                                type='button'
                                variant='outlined'
                                onClick={handleTemplateCancel}
                                className='zitles-button cancel'>
                                Cancel
                            </Button>
                            <Button
                                type='submit'
                                disabled={!values.templateName.trim() || Object.values(values).slice(1).every((v) => !v) || isSubmitting}
                                className={`zitles-button submit ${isSubmitting ? 'disable' : ''}`}
                                startIcon={isSubmitting ? <CircularProgress size={14} color='inherit' /> : null}>
                                {isSubmitting ? 'Creating Template' : 'Submit'}
                            </Button>
                        </DialogActions>
                    </Form>
                );
            }}
        </Formik>
    );

    const renderImportMode = (): JSX.Element => {
        return (
            <Formik
                initialValues={{ selectedTemplate: null as LineListTemplate | null }}
                onSubmit={() => {}}>
                {({ values, setFieldValue }: FormikProps<{ selectedTemplate: LineListTemplate | null }>): JSX.Element => (
                    <Form>
                        <DialogContent dividers>
                            <Box className='space-y-4 py-4'>
                                <Autocomplete
                                    options={filteredTemplates}
                                    getOptionLabel={(option: LineListTemplate): string => option.templateName ?? ''}
                                    value={values.selectedTemplate}
                                    onChange={(event, newValue) => {
                                        setFieldValue('selectedTemplate', newValue);
                                        if (newValue) {
                                            handleTemplateSelect(newValue);
                                        }
                                    }}
                                    renderInput={(params: AutocompleteRenderInputParams): JSX.Element => (
                                        <TextField
                                            {...params}
                                            placeholder='Type to search templates...'
                                            className='zitles-textfield'
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <InputAdornment position='start'>
                                                        <SearchIcon />
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    )}
                                    noOptionsText='No Template Found'
                                    loading={isLoadingTemplates}
                                    loadingText='Loading templates...'/>
                            </Box>
                        </DialogContent>

                        <DialogActions className='action-btn-container'>
                            <Button
                                type='button'
                                variant='outlined'
                                onClick={handleTemplateCancel}
                                className='zitles-button cancel'>
                                Cancel
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        );
    };

    return (
        <Dialog
            open={props.open}
            onClose={handleTemplateCancel}
            maxWidth='md'
            fullWidth
            className='zitles-dialog'>
            <DialogTitle className='zitles-dialog-title'>
                <span>{mode === ManageMode.EXPORT ? 'Export Line List Template' : 'Import Line List Template'}</span>
                <IconButton onClick={handleTemplateCancel}>
                    <CloseIcon className='dialog-icon' />
                </IconButton>
            </DialogTitle>
            {
                isLoadingTemplates &&
                    <LoadingComponent />
            }

            {
                mode === ManageMode.EXPORT
                    ? renderExportMode()
                    : renderImportMode()
            }
        </Dialog>
    );
}

export default observer(LineListTemplateDialog); 